/**
 * DSMS6 Assistant — PHOTON-125.
 *
 * Chat surface for support and ops to change accountTable on DSMS6 docking
 * stations. UI shell only: there is no backend yet (it waits on the Photon ↔
 * TacticalRMM link and Scott's policy decisions, see
 * docs/design/PHOTON-125/PROPOSAL.md), so nothing typed here leaves the
 * browser. Each message gets a plain "not sent" notice — never anything styled
 * as an AI answer, because no model produced it (design language §1.6).
 *
 * Layout follows JiraAssistant (empty state with samples, bubbles, input row)
 * without the thread sidebar, which needs server-side threads.
 */
import React, { useCallback, useRef, useState } from "react";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Textarea,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";
import {
  Send24Regular,
  Add20Regular,
  PlugDisconnected20Regular,
  ServerRegular,
} from "@fluentui/react-icons";

import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  banner: {
    margin: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} 0`,
  },
  messages: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
  },
  emptyIcon: {
    fontSize: "48px",
  },
  requirements: {
    maxWidth: "78ch",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    textAlign: "left",
  },
  requirementList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXXL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground2,
  },
  emptySamples: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxWidth: "640px",
  },
  sampleButton: {
    justifyContent: "flex-start",
    textAlign: "left",
    // Keep the request's line breaks and tab-separated columns visible; a long unbroken
    // value wraps instead of scrolling the panel sideways.
    overflowWrap: "anywhere",
    // Monospace with 8-column tab stops so each field lines up under the one above.
    whiteSpace: "pre-wrap",
    tabSize: 8,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    height: "auto",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  sampleBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  sampleCaption: {
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  messageList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "75%",
    overflowWrap: "anywhere",
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: "16px 16px 4px 16px",
    whiteSpace: "pre-wrap",
  },
  // A pasted request with tab-separated user rows: keep its columns aligned.
  tabular: {
    tabSize: 8,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  notice: {
    alignSelf: "flex-start",
    maxWidth: "92%",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  inputRow: {
    padding: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  textarea: { flex: 1 },
});

interface ChatMessage {
  id: string;
  role: "user" | "notice";
  text: string;
}

// What a change request needs before it can be submitted, in the order a user row is written.
const REQUIRED_INFO = [
  "Docking station host name(s)",
  "Badge ID(s)",
  "User first and last name(s)",
  "Device ID(s)",
  "User ID(s)",
];

// The ideal request: a "Dock Host Names:" line, then one tab-separated row per user,
// Badge ID, First, Last, Device ID, User ID. Made-up values (agency "EXPD").
const userRow = (...cols: string[]) => cols.join("\t");
const request = (hosts: string, ...rows: string[]) =>
  [`Dock Host Names: ${hosts}.`, ...rows].join("\n");

// The caption is shown above each example but not sent. It also starts the button's accessible
// name, so a screen reader hears it instead of every value in the block.
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const describeSample = ({ caption, text }: { caption: string; text: string }) => {
  const [hosts, ...rows] = text.split("\n");
  return `${caption}: ${plural(hosts.split(",").length, "dock")}, ${plural(rows.length, "user")}`;
};

const SAMPLE_REQUESTS = [
  {
    caption: "Several docks, several users",
    text: request(
      "LL-EXPD-DS2, LL-EXPD-DS8, LL-EXPD-DSX2",
      userRow("EX03", "Jane", "Doe", "EXPD116", "LL0116"),
      userRow("EX55", "Alex", "Rivera", "EXPD125", "LL0125"),
      userRow("EX82", "Sam", "Carter", "EXPD015", "LL0015"),
    ),
  },
  {
    caption: "One dock, one user",
    text: request("LL-EXPD-DS4", userRow("EX17", "Pat", "Lee", "EXPD204", "LL0204")),
  },
  {
    caption: "Two docks, two users",
    text: request(
      "LL-EXPD-DS2, LL-EXPD-DS8",
      userRow("EX40", "Morgan", "Diaz", "EXPD131", "LL0131"),
      userRow("EX61", "Chris", "Park", "EXPD142", "LL0142"),
    ),
  },
  {
    // Same officer, Device ID and User ID as the first example, new Badge ID; the name is
    // repeated unchanged. The host names must still be right, or the row won't be found.
    caption: "Badge ID change only (same name, new badge)",
    text: request("LL-EXPD-DS2", userRow("EX97", "Jane", "Doe", "EXPD116", "LL0116")),
  },
];

const NOT_SENT =
  "Not sent. The DSMS6 backend isn't connected yet, so no dock was contacted and nothing was changed.";

export const Dsms6Assistant: React.FC = () => {
  const styles = useStyles();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const nextId = useRef(0);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const submit = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      const n = nextId.current++;
      setMessages((m) => [
        ...m,
        { id: `u_${n}`, role: "user", text },
        { id: `n_${n}`, role: "notice", text: NOT_SENT },
      ]);
      setInput("");
      scrollToBottom();
    },
    [scrollToBottom],
  );

  const newChat = useCallback(() => {
    setMessages([]);
    setInput("");
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit(input);
      }
    },
    [input, submit],
  );

  return (
    <div className={styles.container}>
      <div className={styles.banner}>
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>Preview: not connected</MessageBarTitle>
            This is the DSMS6 interface only. Messages stay in your browser and no
            docking station is contacted.
          </MessageBarBody>
        </MessageBar>
      </div>

      <div ref={scrollRef} className={styles.messages}>
        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <ServerRegular className={styles.emptyIcon} aria-hidden="true" />
            <Text size={500}>Edit DSMS6 docking station accounts.</Text>
            <div className={styles.requirements}>
              <Text size={300}>
                Please provide the following in order to submit a proper change request:
              </Text>
              <ul className={styles.requirementList}>
                {REQUIRED_INFO.map((item) => (
                  <li key={item}>
                    <Text size={300}>{item}</Text>
                  </li>
                ))}
              </ul>
              <Text size={200}>
                List the docks, then one row per user: Badge ID, First, Last, Device ID, User
                ID. Each row finds the account on each dock by its Device ID and User ID, and
                updates its Badge ID and first and last name to the values given. When this is
                connected, every change will be previewed on each dock before anything is
                written.
              </Text>
            </div>
            <div className={styles.emptySamples}>
              {SAMPLE_REQUESTS.map((s) => (
                <Button
                  key={s.caption}
                  className={styles.sampleButton}
                  appearance="subtle"
                  aria-label={describeSample(s)}
                  onClick={() => submit(s.text)}
                >
                  <span className={styles.sampleBody}>
                    <span className={styles.sampleCaption}>{s.caption}</span>
                    <span>{s.text}</span>
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}
        {/* Only the conversation is a live region, so the empty state's samples are never read
            out. It stays mounted even when empty: screen readers often skip announcements from a
            region that appears together with its first content. */}
        <div className={styles.messageList} role="log" aria-live="polite">
          {messages.map((m) =>
            m.role === "user" ? (
              <div
                key={m.id}
                className={mergeClasses(styles.userBubble, m.text.includes("\t") && styles.tabular)}
              >
                {m.text}
              </div>
            ) : (
              <div key={m.id} className={styles.notice}>
                <PlugDisconnected20Regular aria-hidden="true" />
                <span>{m.text}</span>
              </div>
            ),
          )}
        </div>
      </div>

      <div className={styles.inputRow}>
        <Button
          appearance="subtle"
          icon={<Add20Regular />}
          onClick={newChat}
          disabled={messages.length === 0}
        >
          New chat
        </Button>
        <div className={styles.textarea}>
          <Textarea
            value={input}
            onChange={(_, data) => setInput(data.value)}
            onKeyDown={onKeyDown}
            aria-label="Account change request"
            placeholder="Describe the account change and which docks it applies to…"
            resize="vertical"
            rows={2}
            style={{ width: "100%" }}
          />
        </div>
        <VoiceTextInputButton
          value={input}
          onChange={setInput}
          onDictationComplete={(text) => submit(text)}
        />
        <Button
          icon={<Send24Regular />}
          appearance="primary"
          onClick={() => submit(input)}
          disabled={!input.trim()}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

export default Dsms6Assistant;
